import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shadcn/components/ui/form';
import type { SecretResponse } from '@shadcn/hooks/type';
import { useCreateSecrets, useDeleteSecrets, useGetSecrets } from '@shadcn/hooks/use-secrets';
import { secretsSchema } from '@shadcn/lib/schemas/secrets';
import { z } from 'zod';
import { ConfirmationDialog } from '~/components/dialogs/ConfirmationDialog/ConfirmationDialog.client';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { Input } from '~/components/ui/Input';
import { Skeleton } from '~/components/ui/Skeleton';
import { IconButton } from '~/components/ui/IconButton';
import { classNames } from '~/utils/classNames';

export function SecretsManager() {
  const { data: secrets, isLoading, error } = useGetSecrets();
  const { mutate: createSecrets, isPending: isCreating } = useCreateSecrets();
  const { mutate: deleteSecrets, isPending: isDeleting } = useDeleteSecrets();

  const [secretToDelete, setSecretToDelete] = useState<null | string>(null);

  const form = useForm<z.infer<typeof secretsSchema>>({
    resolver: zodResolver(secretsSchema),
    defaultValues: {
      secrets: [{ name: '', value: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'secrets',
  });

  const handleCreateSecrets = (formData: z.infer<typeof secretsSchema>) => {
    createSecrets(
      {
        secrets: formData.secrets,
      },
      {
        onSuccess: () => {
          form.reset({ secrets: [{ name: '', value: '' }] });
        },
      },
    );
  };

  const handleDelete = () => {
    if (secretToDelete) {
      deleteSecrets(
        {
          secretNames: [secretToDelete],
        },
        {
          onSuccess: () => {
            setSecretToDelete(null);
          },
        },
      );
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 pt-4 lg:p-8 lg:pt-8 space-y-4">
        <Skeleton className="h-12 w-full mb-8" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 pt-4 lg:p-8 lg:pt-8">
        <Alert type="danger" title="Error loading secrets">
          There was a problem loading your secrets configuration.
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 pt-4 lg:p-8 lg:pt-8">
      <div className="mb-16">
        <h2 className="text-base lg:text-xl font-semibold mb-1">Add New Secret</h2>
        <p className="text-bolt-ds-textSecondary mb-6 text-sm lg:text-base">
          Add sensitive information like API keys that your app needs to work securely.
        </p>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleCreateSecrets)} className="space-y-2">
            {fields.map((field, index) => (
              <div key={field.id} className="flex items-start space-x-2">
                <FormField
                  control={form.control}
                  name={`secrets.${index}.name`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      {index === 0 && <FormLabel className="mb-4">Name</FormLabel>}
                      <FormControl>
                        <Input placeholder="SECRET_NAME" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`secrets.${index}.value`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      {index === 0 && <FormLabel className="mb-4">Value</FormLabel>}
                      <FormControl>
                        <Input type="password" placeholder="••••••••" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="w-8">
                  {index === 0 && <FormLabel className="opacity-0">Remove</FormLabel>}
                  <IconButton
                    icon="i-heroicons:minus"
                    variant="ghost"
                    onClick={() => remove(index)}
                    disabled={fields.length <= 1}
                    className={classNames('h-11', { 'mt-2': index === 0 })}
                  />
                </div>
              </div>
            ))}
            <div className="flex space-x-2 gap-4 flex-col">
              <Button
                className="w-35 items-center"
                variant="secondary"
                size="md"
                startIcon="i-heroicons:plus"
                onClick={() => append({ name: '', value: '' })}
              >
                Add Secret
              </Button>
            </div>

            <Button type="submit" disabled={isCreating} className="mt-6 ml-auto">
              {isCreating ? 'Creating...' : 'Create Secrets'}
            </Button>
          </form>
        </Form>
      </div>

      <div>
        <h2 className="font-semibold mb-4 lg:text-lg">Existing secrets</h2>
        {secrets && secrets.length > 0 ? (
          secrets.map((secret: SecretResponse) => (
            <div
              key={secret.name}
              className="flex items-center justify-between py-4 border-b border-bolt-elements-borderColor last:border-b-0"
            >
              <div>
                <p className="font-mono text-sm tracking-wider">{secret.name}</p>
                {secret.updated_at && (
                  <p className="text-xs text-bolt-ds-textSecondary mt-1">
                    Last updated: {new Date(secret.updated_at).toLocaleString()}
                  </p>
                )}
              </div>
              <Button
                color="danger"
                variant="tint"
                onClick={() => {
                  setSecretToDelete(secret.name);
                }}
                disabled={isDeleting}
              >
                Delete
              </Button>
            </div>
          ))
        ) : (
          <Alert type="info" title="No secrets found">
            No secrets found for this project.
          </Alert>
        )}
      </div>
      <ConfirmationDialog
        open={!!secretToDelete}
        titleText="Confirm deletion"
        handleConfirm={handleDelete}
        onClose={() => {
          setSecretToDelete(null);
        }}
        isConfirming={isDeleting}
        confirmButtonVariant="danger"
        confirmButtonNode="Delete"
      >
        Are you sure you want to delete the secret <b>{secretToDelete}</b>?
      </ConfirmationDialog>
    </div>
  );
}
